import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as solidIcons from "@fortawesome/free-solid-svg-icons";
import { useBusinessData } from "../../context/BusinessContext";
import { createPaymentLink, verifyPayment } from "../../utils/RazorpayUtil";
import { toast } from "react-toastify";
import PaymentReceipt from "../receipt/PaymentReceipt";
import "../../css/Donation.css";

const Donation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { loading, error, getData, hasData, getImageUrl } = useBusinessData();
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState({
    status: null, // 'success', 'error', 'cancel'
    message: "",
  });

  const [donorInfo, setDonorInfo] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  // Add transaction details state
  const [transactionDetails, setTransactionDetails] = useState(null);

  // Check if donation feature is enabled in businessInfo.json
  const isDonationEnabled = getData("donation.enabled", false);

  // Add ref for thank you section
  const thankYouRef = useRef(null);

  // Add state for payment tracking
  const [trackingPaymentId, setTrackingPaymentId] = useState("");
  const [isTrackingPayment, setIsTrackingPayment] = useState(false);

  // Add new state for tracking status
  const [trackingStatus, setTrackingStatus] = useState({
    status: null, // 'success', 'error', null
    message: "",
  });

  // Add new state for tracking status
  const [shouldScrollToReceipt, setShouldScrollToReceipt] = useState(false);

  // Callback ref that handles scrolling when element becomes available
  const receiptSectionRef = useCallback((node) => {
    if (node && shouldScrollToReceipt) {
      console.log('Receipt section mounted and scroll requested, scrolling...');
      thankYouRef.current = node;
      // Use requestAnimationFrame to ensure DOM is fully updated
      requestAnimationFrame(() => {
        scrollToReceipt();
      });
      setShouldScrollToReceipt(false);
    } else if (node) {
      thankYouRef.current = node;
    }
  }, [shouldScrollToReceipt]);

  // Check for payment success in URL params
  useEffect(() => {
    const verifyPaymentStatus = async () => {
      const params = new URLSearchParams(location.search);
      const status = params.get("status");
      const paymentLinkId = params.get("razorpay_payment_link_id");

      if (status === "success" && paymentLinkId) {
        setProcessingPayment(true);
        try {
          const result = await verifyPayment(paymentLinkId);
          if (result.success) {
            setTransactionDetails({
              donorName: result.customer.name,
              donorEmail: result.customer.email,
              donorPhone: result.customer.contact,
              date: new Date(result.created_at).toLocaleDateString("en-IN"),
              amount: (result.amount / 100).toFixed(2),
              currency: result.currency,
              paymentId: result.payment_id,
              message: result.description,
              status: result.status,
            });
            setPaymentStatus({
              status: "success",
              message: getData(
                "ui.donation.successMessage",
                "Your donation was successful! Thank you for your support."
              ),
            });
            setProcessingPayment(false);
            // Set flag to scroll to receipt after it's rendered
            setShouldScrollToReceipt(true);
            // Clear the URL parameters
            navigate("/donate", { replace: true });
          } else {
            toast.error(result.error || "Payment verification failed");
            setPaymentStatus({
              status: "error",
              message:
                result.error ||
                getData(
                  "ui.donation.errorMessage",
                  "There was an error processing your donation. Please try again."
                ),
            });
          }
        } catch (error) {
          console.error("Error verifying payment:", error);
          toast.error("Error verifying payment");
          setPaymentStatus({
            status: "error",
            message:
              error.message ||
              getData(
                "ui.donation.errorMessage",
                "There was an error verifying your payment. Please contact support."
              ),
          });
        } finally {
          setProcessingPayment(false);
        }
      }
    };

    verifyPaymentStatus();
  }, [location, navigate]);

  useEffect(() => {
    // Reset state when component mounts
    setSelectedAmount(null);
    setCustomAmount("");
    setPaymentStatus({ status: null, message: "" });
    setTransactionDetails(null);
  }, []);



  // Add effect to handle body class for payment processing
  useEffect(() => {
    if (processingPayment) {
      document.body.classList.add("payment-processing-active");
    } else {
      document.body.classList.remove("payment-processing-active");
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove("payment-processing-active");
    };
  }, [processingPayment]);

  // Handler for selecting a preset amount
  const handleAmountSelect = (amount) => {
    setSelectedAmount(amount);
    setCustomAmount("");
  };

  // Handler for custom amount changes
  const handleCustomAmountChange = (e) => {
    const value = e.target.value;
    // Only accept numbers in the input
    if (/^\d*$/.test(value)) {
      setCustomAmount(value);
      setSelectedAmount(null);
    }
  };

  // Handler for donor information changes
  const handleDonorInfoChange = (e) => {
    const { name, value } = e.target;
    setDonorInfo({
      ...donorInfo,
      [name]: value,
    });
  };

  // Handler for processing the payment
  const handlePaymentSubmit = async (e) => {
    e.preventDefault();

    // Determine final amount (either selected preset or custom amount)
    const finalAmount = selectedAmount || parseInt(customAmount, 10);

    // Validate amount
    if (!finalAmount || finalAmount <= 0) {
      setPaymentStatus({
        status: "error",
        message: "Please select or enter a valid donation amount",
      });
      return;
    }

    // Validate required donor information
    if (!donorInfo.name || !donorInfo.email || !donorInfo.phone) {
      setPaymentStatus({
        status: "error",
        message: "Please provide your name, email, and phone number",
      });
      return;
    }

    // Start payment processing
    setProcessingPayment(true);
    setPaymentStatus({ status: null, message: "" });

    try {
      // Get currency from businessInfo.json
      const currency = getData("donation.currency", "INR");
      const businessName = getData("name", "BusinessInfo");

      // Create payment link with RazorPay
      const result = await createPaymentLink({
        amount: finalAmount,
        currency,
        name: donorInfo.name,
        email: donorInfo.email,
        phone: donorInfo.phone,
        businessName,
        description: donorInfo.message || `Donation to ${businessName}`,
      });

      // Always ensure we're setting processingPayment to false
      setProcessingPayment(false);

      if (!result.success) {
        setPaymentStatus({
          status: "error",
          message:
            result.error ||
            getData(
              "ui.donation.errorMessage",
              "There was an error creating the payment link. Please try again."
            ),
        });
      }
      // The redirect to Razorpay's payment page will happen automatically in createPaymentLink
    } catch (error) {
      console.error("Payment error:", error);
      setProcessingPayment(false);

      setPaymentStatus({
        status: "error",
        message:
          error.message ||
          getData(
            "ui.donation.errorMessage",
            "There was an error processing your donation. Please try again."
          ),
      });
    }
  };

  // Function to track payment status
  const handleTrackPayment = async (e) => {
    e.preventDefault();
    if (!trackingPaymentId) {
      setTrackingStatus({
        status: 'error',
        message: 'Please enter a payment ID'
      });
      return;
    }

    if (!trackingPaymentId.startsWith('pay_')) {
      setTrackingStatus({
        status: 'error',
        message: 'Invalid payment ID format. Payment ID should start with "pay_"'
      });
      return;
    }

    setIsTrackingPayment(true);
    setTrackingStatus({ status: null, message: '' });
    
    try {
      const result = await verifyPayment(trackingPaymentId);
      console.log('Payment verification result:', result);
      
      if (result.success) {
        // Get the actual amount paid (excluding Razorpay fee)
        const actualAmount = result.amount_paid || result.amount;
        
        // Format transaction details from the API response
        setTransactionDetails({
          donorName: result.customer.name,
          donorEmail: result.customer.email,
          donorPhone: result.customer.contact,
          created_at: result.created_at,
          amount: (actualAmount / 100).toFixed(2), // Use amount_paid instead of amount
          currency: result.currency,
          paymentId: result.payment_id,
          message: result.description || result.notes?.donor_message,
          status: result.status === 'captured' ? 'Successful' : result.status
        });
        
        setTrackingStatus({
          status: 'success',
          message: 'Payment details found!'
        });
        
        // Set flag to scroll to receipt after it's rendered
        setShouldScrollToReceipt(true);
      } else {
        setTrackingStatus({
          status: 'error',
          message: result.error || 'Payment not found'
        });
        setTransactionDetails(null);
      }
    } catch (error) {
      console.error('Error tracking payment:', error);
      setTrackingStatus({
        status: 'error',
        message: error.message || 'Error tracking payment'
      });
      setTransactionDetails(null);
    } finally {
      setIsTrackingPayment(false);
    }
  };

  // Function to handle smooth scrolling with fallback
  const scrollToReceipt = () => {
    console.log('Scrolling to receipt section...');
    try {
      // First try modern smooth scrolling
      if ('scrollBehavior' in document.documentElement.style) {
        console.log('Using native smooth scrolling');
        thankYouRef.current.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      } else {
        console.log('Using fallback smooth scrolling');
        // Fallback for browsers that don't support smooth scrolling
        const element = thankYouRef.current;
        const elementPosition = element.offsetTop;
        const offsetPosition = elementPosition - 100; // Add some padding from top
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    } catch (error) {
      // Ultimate fallback - just scroll to the element
      console.warn('Smooth scrolling failed, using fallback:', error);
      thankYouRef.current.scrollIntoView();
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (error) {
    return <div className="error">Error loading data: {error}</div>;
  }

  // If donation feature is disabled, show a message
  if (!isDonationEnabled) {
    return (
      <div
        className="container"
        style={{ padding: "100px 20px", textAlign: "center" }}
      >
        <h2>Donation feature is currently disabled</h2>
        <p>Please check back later or contact us for more information.</p>
        <Link to="/" className="btn btn-primary" style={{ marginTop: "20px" }}>
          Return to Home
        </Link>
      </div>
    );
  }

  // Get donation hero background image from JSON
  const donationHeroImage = getImageUrl(
    "images.backgrounds.donation",
    "donation-bg.jpg"
  );

  // Create inline style for background image
  const donationHeroStyle = {
    backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url('${donationHeroImage}')`,
  };

  // Get donation amounts from JSON
  const donationAmounts = getData("donation.amounts", [
    { value: 100, label: "Basic Supporter" },
    { value: 250, label: "Silver Supporter" },
    { value: 500, label: "Gold Supporter" },
    { value: 1000, label: "Platinum Supporter" },
  ]);

  return (
    <div className="donation-page">
      {processingPayment ? (
        <div className="verifying-payment">
          <div className="spinner"></div>
          <h2>Verifying your payment...</h2>
        </div>
      ) : (
        <>
          {/* Donation Hero */}
          <div className="donation-hero" style={donationHeroStyle}>
            <div className="container">
              <div className="donation-hero-content">
                <h1>{getData("ui.donation.pageTitle", "Donate Now")}</h1>
                <p>
                  {getData(
                    "ui.donation.pageSubtitle",
                    "Support our mission with your contribution"
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Donation Form Section */}
          <section className="section">
            <div className="container">
              {/* Show error message if payment failed - only for donation, not tracking */}
              {paymentStatus.status === "error" && !trackingPaymentId && (
                <div className="donation-message donation-error">
                  <p>{paymentStatus.message}</p>
                </div>
              )}

              {/* Show processing payment message during payment */}
              {processingPayment && (
                <div className="payment-processing">
                  <h3>
                    {getData(
                      "ui.donation.processingPaymentMessage",
                      "Processing payment..."
                    )}
                  </h3>
                  <div className="spinner"></div>
                </div>
              )}

              {!transactionDetails && (
                <div className="donation-container">
                  <div className="donation-info">
                    <h2>{getData("donation.title", "Support Our Cause")}</h2>
                    <p>
                      {getData(
                        "donation.description",
                        "We appreciate your support in helping us achieve our mission. Your donation will be used to further our goals and make a positive impact in our community."
                      )}
                    </p>

                    <h3>
                      {getData(
                        "ui.donation.selectAmountLabel",
                        "Select an Amount"
                      )}
                    </h3>
                    <div className="donation-amounts">
                      {donationAmounts.map((option, index) => (
                        <div
                          key={index}
                          className={`amount-option ${
                            selectedAmount === option.value ? "selected" : ""
                          }`}
                          onClick={() => handleAmountSelect(option.value)}
                        >
                          <div className="amount-value">
                            {getData("donation.currency", "INR")} {option.value}
                          </div>
                          <div className="amount-label">{option.label}</div>
                        </div>
                      ))}
                    </div>

                    <div className="custom-amount">
                      <label htmlFor="customAmount">
                        {getData(
                          "ui.donation.customAmountLabel",
                          "Custom Amount"
                        )}
                      </label>
                      <input
                        type="text"
                        id="customAmount"
                        value={customAmount}
                        onChange={handleCustomAmountChange}
                        placeholder="Enter amount"
                      />
                    </div>
                  </div>

                  <div className="donation-form-container">
                    <h2>
                      {getData(
                        "ui.donation.donorDetailsTitle",
                        "Your Information"
                      )}
                    </h2>
                    <form
                      className="donation-form"
                      onSubmit={handlePaymentSubmit}
                    >
                      <div className="form-group">
                        <label htmlFor="name">
                          {getData("ui.donation.donorNameLabel", "Full Name")}
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={donorInfo.name}
                          onChange={handleDonorInfoChange}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="email">
                          {getData(
                            "ui.donation.donorEmailLabel",
                            "Email Address"
                          )}
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={donorInfo.email}
                          onChange={handleDonorInfoChange}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="phone">
                          {getData(
                            "ui.donation.donorPhoneLabel",
                            "Phone Number"
                          )}
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={donorInfo.phone}
                          onChange={handleDonorInfoChange}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="message">
                          {getData(
                            "ui.donation.donorMessageLabel",
                            "Message (Optional)"
                          )}
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          value={donorInfo.message}
                          onChange={handleDonorInfoChange}
                          rows="3"
                        ></textarea>
                      </div>

                      <button
                        type="submit"
                        className={`btn btn-primary ${
                          (!selectedAmount && !customAmount) ||
                          processingPayment
                            ? "btn-disabled"
                            : ""
                        }`}
                        disabled={
                          (!selectedAmount && !customAmount) ||
                          processingPayment
                        }
                      >
                        {getData("ui.donation.payNowButton", "Donate Now")}
                      </button>
                    </form>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Receipt Section */}
          {transactionDetails && (
            <section className="section" ref={receiptSectionRef}>
              <div className="container">
                <PaymentReceipt
                  transactionDetails={transactionDetails}
                  showThankYou={true}
                  onMakeAnotherDonation={() => {
                    setTransactionDetails(null);
                    setTrackingPaymentId("");
                    setTrackingStatus({ status: null, message: "" });
                    setPaymentStatus({ status: null, message: "" });
                  }}
                />
              </div>
            </section>
          )}

          {/* Payment Tracking Section */}
          <section className="section payment-tracking">
            <div className="container">
              <div className="payment-tracking-container">
                <h2>Track Your Payment</h2>
                <p>
                  Enter your payment ID to check the status of your donation
                </p>
                <p className="payment-tracking-hint">
                  Payment ID starts with 'pay_' (e.g., pay_ABC123...)
                </p>

                {/* Show tracking status message */}
                {trackingStatus.message && (
                  <div
                    className={`payment-tracking-message ${
                      trackingStatus.status === "success" ? "success" : "error"
                    }`}
                  >
                    {trackingStatus.message}
                  </div>
                )}

                <form onSubmit={handleTrackPayment} className="payment-tracking-form">
                  <div className="form-group">
                    <input
                      type="text"
                      value={trackingPaymentId}
                      onChange={(e) => setTrackingPaymentId(e.target.value)}
                      placeholder="Enter Payment ID"
                      className="payment-tracking-input"
                    />
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={
                        isTrackingPayment ||
                        !trackingPaymentId.startsWith("pay_")
                      }
                    >
                      {isTrackingPayment ? "Tracking..." : "Track Payment"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default Donation;
